'use client';

import type { TranslationKey } from '@freedraw/i18n';
import { useAppTranslation } from '@freedraw/i18n/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowRight, Sparkles } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';

import { DiagramPreview, type DiagramKind } from '@/components/landing/landing-diagrams';
import { DISPLAY_FONT } from '@/components/landing/landing-fonts';
import { LandingParallaxShape } from '@/components/landing/landing-parallax-shapes';
import { BRAND, brandTint } from '@/theme';

const INK = BRAND.ink;
const SUBTLE = '#596275';

const KIND_KEYS: Record<DiagramKind, { type: TranslationKey; prompt: TranslationKey; labels: TranslationKey }> = {
  flowchart: { type: 'landing.typeFlowchart', prompt: 'landing.makePromptFlowchart', labels: 'landing.makeLabelsFlowchart' },
  mindmap: { type: 'landing.typeMindmap', prompt: 'landing.makePromptMindmap', labels: 'landing.makeLabelsMindmap' },
  sequence: { type: 'landing.typeSequence', prompt: 'landing.makePromptSequence', labels: 'landing.makeLabelsSequence' },
  timeline: { type: 'landing.typeTimeline', prompt: 'landing.makePromptTimeline', labels: 'landing.makeLabelsTimeline' },
  kanban: { type: 'landing.typeKanban', prompt: 'landing.makePromptKanban', labels: 'landing.makeLabelsKanban' },
  gantt: { type: 'landing.typeGantt', prompt: 'landing.makePromptGantt', labels: 'landing.makeLabelsGantt' },
  er: { type: 'landing.typeEr', prompt: 'landing.makePromptEr', labels: 'landing.makeLabelsEr' },
  pie: { type: 'landing.typePie', prompt: 'landing.makePromptPie', labels: 'landing.makeLabelsPie' },
  org: { type: 'landing.typeOrg', prompt: 'landing.makePromptOrg', labels: 'landing.makeLabelsOrg' }
};

// Ideas pick the diagram that suits them; the same diagram can be reached either way.
const IDEAS: Array<{ key: TranslationKey; kind: DiagramKind }> = [
  { key: 'landing.useCasePlanProject', kind: 'gantt' },
  { key: 'landing.useCaseBrainstorm', kind: 'mindmap' },
  { key: 'landing.useCaseMapProcess', kind: 'flowchart' },
  { key: 'landing.useCaseOrgChart', kind: 'org' },
  { key: 'landing.useCaseTimeline', kind: 'timeline' },
  { key: 'landing.useCaseMeeting', kind: 'kanban' },
  { key: 'landing.useCaseExplain', kind: 'sequence' }
];

const TYPES: DiagramKind[] = ['flowchart', 'mindmap', 'sequence', 'timeline', 'kanban', 'gantt', 'er', 'pie', 'org'];

type Choice = { chip: string; kind: DiagramKind };

function PickerChip({ label, selected, onClick, testId }: {
  label: string;
  selected: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={selected}
      data-testid={testId}
      sx={{
        px: 1.75,
        height: 40,
        borderRadius: '10px',
        border: `1.5px solid ${selected ? BRAND.pinkStrong : '#DEE3EE'}`,
        bgcolor: selected ? BRAND.pinkLight : '#ffffff',
        color: INK,
        fontSize: 14.5,
        fontWeight: 600,
        fontFamily: 'inherit',
        transition: 'border-color 120ms ease, background-color 120ms ease, transform 120ms ease',
        '&:hover': { borderColor: selected ? BRAND.pinkStrong : BRAND.pinkDark, transform: 'translateY(-1px)' },
        '&.Mui-focusVisible': { outline: `2px solid ${BRAND.pinkStrong}`, outlineOffset: 2 }
      }}
    >
      {label}
    </ButtonBase>
  );
}

export function LandingShowcaseSection({ ctaHref }: { ctaHref: string }) {
  const { t } = useAppTranslation();
  const [choice, setChoice] = useState<Choice>({ chip: IDEAS[0]!.key, kind: IDEAS[0]!.kind });
  const keys = KIND_KEYS[choice.kind];

  return (
    <Box component="section" id="diagrams" sx={{
      scrollMarginTop: 72, position: 'relative', isolation: 'isolate',
      overflow: 'hidden', py: { xs: 10, md: 17 },
      bgcolor: '#E4F3E9', borderBlock: '1px solid rgba(23,26,34,0.12)',
      backgroundImage: 'radial-gradient(rgba(23,26,34,0.09) 1px, transparent 1px)', backgroundSize: '29px 29px'
    }}>
      <Box data-parallax="65" aria-hidden="true" sx={{ position: 'absolute', pointerEvents: 'none', zIndex: -1,
        right: -175, top: -140, width: { xs: 340, md: 510 }, height: { xs: 340, md: 510 }, opacity: 0.33 }}>
        <LandingParallaxShape variant="orbit" color="#178861" />
      </Box>
      <Box data-parallax="-40" aria-hidden="true" sx={{ position: 'absolute', pointerEvents: 'none', zIndex: -1,
        left: -115, bottom: -110, width: 320, height: 320, opacity: 0.48 }}>
        <LandingParallaxShape variant="ribbon" color="#C18B19" />
      </Box>
      <Container maxWidth="xl" sx={{ position: 'relative', px: { xs: 2.5, sm: 4, lg: 7 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 6, md: 9 }} alignItems={{ md: 'center' }}>
          <Box data-reveal sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: BRAND.pinkStrong, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {t('landing.makeEyebrow')}
            </Typography>
            <Typography
              component="h2"
              sx={{ mt: 1.5, fontFamily: DISPLAY_FONT, textWrap: 'balance', fontWeight: 600, fontSize: { xs: 43, sm: 55, md: 75 }, lineHeight: 1.03, letterSpacing: '-0.058em' }}
            >
              {t('landing.makeTitle')}
            </Typography>

            <Typography sx={{ mt: 4, mb: 1.5, fontSize: 15, fontWeight: 700 }}>{t('landing.makeIdeasLabel')}</Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {IDEAS.map(({ key, kind }) => (
                <PickerChip
                  key={key}
                  label={t(key)}
                  selected={choice.chip === key}
                  onClick={() => setChoice({ chip: key, kind })}
                  testId={`landing-idea-${kind}`}
                />
              ))}
            </Stack>

            <Typography sx={{ mt: 4, mb: 1.5, fontSize: 15, fontWeight: 700 }}>{t('landing.makeTypesLabel')}</Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {TYPES.map((kind) => (
                <PickerChip
                  key={kind}
                  label={t(KIND_KEYS[kind].type)}
                  selected={choice.chip === kind}
                  onClick={() => setChoice({ chip: kind, kind })}
                  testId={`landing-type-${kind}`}
                />
              ))}
            </Stack>
          </Box>

          <Stack data-reveal alignItems="center" spacing={2.5} sx={{ flex: 1.05, width: '100%', minWidth: 0 }}>
            <Box
              data-testid="landing-diagram-preview"
              data-kind={choice.kind}
              sx={{
                width: '100%',
                borderRadius: '23px',
                border: '2px solid #171A22',
                bgcolor: '#FCFAF4',
                backgroundImage: `radial-gradient(${brandTint(0.3)} 1px, transparent 1px)`,
                backgroundSize: '18px 18px',
                boxShadow: '13px 16px 0 rgba(23,26,34,0.14), 0 24px 60px rgba(23,26,34,0.08)',
                overflow: 'hidden'
              }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{ m: { xs: 1.5, sm: 2 }, p: 1.5, borderRadius: '12px', bgcolor: '#ffffff', border: '1px solid #E7EAFD' }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: '8px',
                    bgcolor: INK,
                    color: BRAND.pink,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Sparkles size={15} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: SUBTLE }}>{t('landing.makeYouAsk')}</Typography>
                  <Typography sx={{ fontSize: 15, lineHeight: 1.4 }}>{t(keys.prompt)}</Typography>
                </Box>
              </Stack>
              <Box
                key={choice.kind}
                role="img"
                aria-label={t('landing.makePreviewLabel', { type: t(keys.type) })}
                sx={{
                  px: { xs: 2, sm: 4 },
                  pt: 1,
                  pb: { xs: 2.5, sm: 4 },
                  aspectRatio: '400 / 280',
                  animation: 'landing-preview-in 380ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                  '@keyframes landing-preview-in': {
                    from: { opacity: 0, transform: 'translateY(10px) scale(0.98)' }
                  },
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
                }}
              >
                <DiagramPreview kind={choice.kind} labels={t(keys.labels).split('|')} />
              </Box>
            </Box>
            <Button
              component={Link}
              href={ctaHref}
              variant="contained"
              color="secondary"
              endIcon={<ArrowRight size={17} />}
              data-testid="landing-showcase-cta"
              sx={{ borderRadius: '10px', px: 2.75, height: 46, fontSize: 15, fontWeight: 700, color: '#ffffff' }}
            >
              {t('landing.makeOpen')}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
